import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useTheme } from 'styled-components';
import { useGetTicketSalesReportV2 } from 'components/Participants/gql/useGetTicketSalesReportV2.mutation';
import {
  PageShell,
  PageHeader,
  BreakdownTable,
  DateCard,
  eventDateCardProps,
  Icon,
  tokens,
  useIsMobile,
} from 'components/v3';
import { TicketBreakdownReportPrint } from './TicketBreakdownReportPrint';

// Ticket Breakdown report — gen-3 redesign. Quantities only (no prices),
// counting valid scannable tickets. Three sections:
//   1. Ticket Breakdown — sold tickets grouped by start/end date (single-day
//      groups first, then multiday spans), bundle children excluded.
//   2. Tickets by Day — per-day gate counts; bundle-type tickets excluded,
//      their per-day children counted on the day they belong to.
//   3. Tier Breakdown — tickets with tiers and their per-tier quantities.

// Event context card (date chip + name + track), same pattern as the gen-3
// Box Office report. On mobile the chip and logo share the top row and the
// name gets the full card width underneath, so titles wrap naturally
// instead of being squeezed between the two.
const EventCard = ({ event }) => {
  const t = tokens(useTheme());
  const isMobile = useIsMobile();
  if (!event) return null;
  const dateProps = eventDateCardProps(event);

  const nameBlock = (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: isMobile ? 18 : 20,
          fontWeight: 800,
          color: t.text,
          lineHeight: 1.25,
        }}
      >
        {event.name}
      </div>
      {event.track?.name ? (
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 500,
            color: t.sub,
            marginTop: 3,
          }}
        >
          {event.track.name}
        </div>
      ) : null}
    </div>
  );

  const logo = event.logo ? (
    <img
      src={event.logo}
      alt={event.name || ''}
      style={{
        width: isMobile ? 76 : 96,
        height: isMobile ? 48 : 60,
        objectFit: 'contain',
        flexShrink: 0,
        marginLeft: 'auto',
      }}
    />
  ) : null;

  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: isMobile ? '16px 18px' : '18px 22px',
        marginBottom: 20,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? 14 : 18,
      }}
    >
      {isMobile ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {dateProps ? <DateCard {...dateProps} /> : null}
            {logo}
          </div>
          {nameBlock}
        </>
      ) : (
        <>
          {dateProps ? <DateCard {...dateProps} /> : null}
          {nameBlock}
          {logo}
        </>
      )}
    </div>
  );
};

// Orange primary export button (handed to the print component's render
// slot). Full "Export PDF" on desktop; a square icon-only button on mobile
// so it sits inline with the page title.
const ExportPdfButton = ({ compact }) => (
  <span
    title="Export PDF"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 9,
      height: compact ? 42 : 46,
      width: compact ? 42 : 'auto',
      padding: compact ? 0 : '0 22px',
      borderRadius: 11,
      background: '#fa4616',
      color: '#fff',
      fontSize: 14.5,
      fontWeight: 700,
      boxShadow: '0 6px 16px rgba(250,70,22,0.28)',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    <Icon name="download" size={18} color="#fff" />
    {compact ? null : 'Export PDF'}
  </span>
);

// Visible failure state with a retry, instead of an endless Loading row.
const ErrorCard = ({ onRetry }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: '26px 22px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 600, color: t.sub, flex: 1 }}>
        The report failed to load.
      </div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          height: 40,
          padding: '0 18px',
          borderRadius: 10,
          border: 'none',
          background: '#fa4616',
          color: '#fff',
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  );
};

// Ticket Hoss sells tickets; every other platform sells passes.
const IS_TICKETHOSS = 'tickethoss' === process.env.REACT_APP_PLATFORM;
const UNIT = IS_TICKETHOSS ? 'Ticket' : 'Pass';
const UNITS = IS_TICKETHOSS ? 'Tickets' : 'Passes';

const ticketGroupsToTable = (groups) =>
  (groups || []).map((group) => ({
    label: group.label,
    total: group.total,
    rows: (group.tickets || []).map((ticket) => ({
      name: ticket.name,
      qty: ticket.qty,
      price: ticket.price,
    })),
  }));

const TicketBreakdownReport = () => {
  const [report, setReport] = useState();
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const { id } = useParams();

  const getTicketSalesReportV2 = useGetTicketSalesReportV2();
  // Two columns on desktop keeps the rows short, so quantities sit close to
  // their labels; stacked below 1100px.
  const narrow = useIsMobile(1100);
  // True mobile (same breakpoint as PageHeader): icon-only export button.
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;
    const getData = async () => {
      try {
        const response = await getTicketSalesReportV2(id);
        if (cancelled) return;
        setReport(response.data?.getTicketSalesReportV2);
        setError(null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('getTicketSalesReportV2 failed', e);
        if (!cancelled) setError(e);
      }
    };
    getData();
    return () => {
      cancelled = true;
    };
  }, [getTicketSalesReportV2, id, attempt]);

  const loading = !report && !error;
  const event = report?.event;

  const tierGroups = report?.tier_breakdown?.length
    ? [
        {
          rows: report.tier_breakdown.map((ticket) => ({
            name: ticket.name,
            qty: ticket.qty,
            tiers: ticket.tiers,
          })),
        },
      ]
    : [];

  return (
    <PageShell>
      <PageHeader icon="file-text" title={`${UNIT} Breakdown`} inlineActions>
        {report && report.tickets_sold > 0 ? (
          <TicketBreakdownReportPrint
            date={event?.start_date}
            report={report}
            button={<ExportPdfButton compact={isMobile} />}
          />
        ) : null}
      </PageHeader>

      <EventCard event={event} />

      {error && !report ? (
        <ErrorCard
          onRetry={() => {
            setError(null);
            setAttempt((n) => n + 1);
          }}
        />
      ) : null}

      {error && !report ? null : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
            columnGap: 20,
            alignItems: 'start',
          }}
        >
          <BreakdownTable
            title={`Total ${UNIT} Breakdown`}
            showPrice
            stat={
              loading
                ? null
                : { label: `${UNITS} Sold`, value: report.tickets_sold }
            }
            columnLabel={UNIT}
            unitLabel={UNITS.toLowerCase()}
            groups={loading ? [] : ticketGroupsToTable(report.ticket_groups)}
            footer={
              loading ? null : { label: 'Total', value: report.tickets_sold }
            }
            loading={loading}
            emptyLabel={`No ${UNITS.toLowerCase()} sold.`}
          />

          <div>
            <BreakdownTable
              title={`${UNITS} by Day`}
              columnLabel={UNIT}
              unitLabel={UNITS.toLowerCase()}
              groups={loading ? [] : ticketGroupsToTable(report.days)}
              loading={loading}
              emptyLabel={`No ${UNITS.toLowerCase()} sold.`}
            />

            {!loading && 0 === tierGroups.length ? null : (
              <BreakdownTable
                title="Tier Breakdown"
                showPrice
                columnLabel={`${UNIT} / Tier`}
                unitLabel={UNITS.toLowerCase()}
                groups={tierGroups}
                loading={loading}
                emptyLabel={`No ${UNITS.toLowerCase()} with tiers.`}
              />
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default TicketBreakdownReport;
